import React, { FunctionComponent } from 'react';
import { Col, Hero, LinkButton } from '@visalex/core';

import cx from 'clsx';
import { useTranslation } from 'next-i18next';

import { Layout } from '../Layout';

import KeyProblemsSection from './Common/KeyProblemsSection/KeyProblemsSection';
import ProcessStepsSection from './Common/ProcessStepsSection/ProcessStepsSection';
import StartOptionsSection from './Common/StartOptionsSection/StartOptionsSection';

import styles from './VisaPage.module.scss';

export type VisaPageProcessStep = {
  imageUrl: string;
  imageAlt: string;
  title: string;
  description: string;
};

export type VisaPageKeyProblem = {
  title: string;
  imageUrl: string;
  imageAlt: string;
  imageName?: string;
  textParts: string[];
};

export type StartOption = {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  video?: {
    linkTitle: string;
    restText: string;
  };
};

type VisaPageProps = {
  seoTitle: string;
  seoDescription: string;
  title: string;
  subtitle: string;
  headerClassname?: string;
  processSection: {
    title: string;
    subtitle: string;
    steps: VisaPageProcessStep[];
  };
  keyProblemsSection: {
    title: string;
    subtitle: string;
    imageFirst: boolean;
    lastItemMdHorizontal: boolean;
    problemsList: VisaPageKeyProblem[];
  };
  startOptionsSection: {
    title: string;
    options: StartOption[];
  };
};

const VisaPage: FunctionComponent<VisaPageProps> = ({
  seoTitle,
  seoDescription,
  headerClassname,
  title,
  subtitle,
  processSection,
  keyProblemsSection,
  startOptionsSection,
}) => {
  const { t } = useTranslation();

  return (
    <Layout title={seoTitle} description={seoDescription}>
      <div className={styles['visa-page-background']}>
        <div>
          <Hero className={cx(styles['visa-page'], headerClassname)}>
            <Col md={12} lg={10} className="mx-auto">
              <h1 className={styles['visa-page__title']}>{title}</h1>
              <p className={styles['visa-page__subtitle']}>{subtitle}</p>
              <div className={styles['visa-page__hero-buttons']}>
                <LinkButton href="/free-consultation" size="lg" className={styles['visa-page__hero-button']}>
                  {t('nav-bar.schedule-consultation')}
                </LinkButton>
                <small className={styles['visa-page__supported-locales']}>{t('common.supported-locales')}</small>
              </div>
            </Col>
          </Hero>
        </div>
        <ProcessStepsSection {...processSection} />
        <KeyProblemsSection {...keyProblemsSection} />
        <StartOptionsSection startOptions={startOptionsSection.options} title={startOptionsSection.title} />
      </div>
    </Layout>
  );
};

export default VisaPage;
